import React from 'react';
import { C } from '../data/constants.js';
import type { HeatmapCell } from '../data/constants.js';
import { EmptyState } from './EmptyState.js';
import { Calendar } from 'lucide-react';

function getColor(val: number): string {
  if (val === 0) return 'rgba(255,255,255,0.02)';
  if (val < 20) return `${C.accent}15`;
  if (val < 40) return `${C.accent}30`;
  if (val < 60) return `${C.accent}50`;
  if (val < 80) return `${C.accent}80`;
  return `${C.accent}CC`;
}

const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

interface ComplianceHeatmapProps {
  data?: HeatmapCell[];
  weeks?: number;
}

export function ComplianceHeatmap({ data, weeks }: ComplianceHeatmapProps): React.ReactElement {
  if (!data || !weeks || data.length === 0) {
    return (
      <EmptyState
        icon={Calendar}
        title="Heatmap Not Available"
        message="Compliance usage data is not yet being collected for this facility."
      />
    );
  }

  return (
    <div
      style={{
        background: C.card,
        border: `1px solid ${C.cardBorder}`,
        borderRadius: 16,
        padding: '20px 24px',
        animation: 'insights-fadeSlideUp 0.5s ease 100ms both',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <div>
          <h4
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: C.textMid,
              margin: 0,
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            eXeX Usage Heatmap
          </h4>
          <p style={{ fontSize: 11, color: C.textDim, margin: '4px 0 0' }}>
            % of eligible surgeries using eXeX by day
          </p>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 10,
            color: C.textFaint,
          }}
        >
          <span>Low</span>
          {[15, 30, 50, 80, 204].map((a, i) => (
            <div
              key={i}
              style={{
                width: 14,
                height: 14,
                borderRadius: 3,
                background: `rgba(0,207,160,${a / 255})`,
              }}
            />
          ))}
          <span>High</span>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 4 }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            marginRight: 6,
            paddingTop: 2,
          }}
        >
          {days.map((d) => (
            <div
              key={d}
              style={{
                height: 18,
                fontSize: 10,
                color: C.textFaint,
                display: 'flex',
                alignItems: 'center',
                fontFamily: "'Space Mono', monospace",
              }}
            >
              {d}
            </div>
          ))}
        </div>
        {Array.from({ length: weeks }, (_, w) => (
          <div key={w} style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
            {Array.from({ length: 7 }, (_, d) => {
              const cell = data.find((c) => c.week === w && c.weekday === d);
              return (
                <div
                  key={d}
                  title={cell ? `${cell.value}% utilization` : ''}
                  style={{
                    height: 18,
                    borderRadius: 4,
                    background: cell ? getColor(cell.value) : 'rgba(255,255,255,0.02)',
                    transition: 'transform 0.15s',
                    cursor: 'default',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                />
              );
            })}
          </div>
        ))}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: 8,
          fontSize: 10,
          color: C.textFaint,
          fontFamily: "'Space Mono', monospace",
        }}
      >
        <span>{weeks} weeks ago</span>
        <span>This week</span>
      </div>
    </div>
  );
}
